// src/pages/FindOncologistsPage.jsx
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  MapPin, 
  Stethoscope, 
  CheckCircle2, 
  ArrowRight, 
  ShieldCheck, 
  Building2, 
  Phone, 
  ExternalLink,
  Filter,
  Users,
  ChevronRight,
  HelpCircle,
  FileText
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { US_STATES } from '@/data/usStatesData';
import { 
  ONCOLOGY_TAXONOMIES, 
  QUALIFIED_CITIES, 
  ONCOLOGISTS_BY_STATE 
} from '@/data/oncologistsData';
import Footer from '@/components/Footer';

export default function FindOncologistsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedState, setSelectedState] = useState('all');

  const pageUrl = `${SITE_ORIGIN}/find-oncologists`;

  const filteredStates = useMemo(() => {
    return US_STATES.filter((st) => {
      if (selectedState !== 'all' && st.slug !== selectedState) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        st.name.toLowerCase().includes(q) ||
        st.abbr.toLowerCase().includes(q) ||
        st.majorCities.some((c) => c.toLowerCase().includes(q))
      );
    });
  }, [selectedState, searchTerm]);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: 'Find Cancer Doctors & Leading Oncologists in the US | NPPES Registry Directory',
      description: 'Directory of board-certified medical oncologists, radiation oncologists, and surgical oncologists across all 50 US states and major cities, verified via the CMS NPPES NPI Registry.',
      url: pageUrl,
      about: {
        '@type': 'MedicalSpecialty',
        name: 'Oncology',
      },
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Oncology Care Directory',
        url: SITE_ORIGIN,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Find Oncologists', item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the difference between a medical oncologist, radiation oncologist, and surgical oncologist?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A medical oncologist specializes in systemic cancer therapies including chemotherapy, immunotherapy, and targeted molecular inhibitors. A surgical oncologist performs biopsies and complex tumor resections. A radiation oncologist designs high-precision radiation therapy protocols such as IMRT, stereotactic body radiation therapy (SBRT), and proton therapy.',
          },
        },
        {
          '@type': 'Question',
          name: 'How does ByOnco verify oncologist credentials?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Our directory indexes board-certified oncology providers using the official CMS National Plan and Provider Enumeration System (NPPES) NPI Registry, cross-referenced with healthcare taxonomy codes for Medical Oncology (207RX0202X), Radiation Oncology (2085R0001X), Surgical Oncology (2086X0206X), Hematology-Oncology (207RH0003X), and Gynecologic Oncology (207VX0201X).',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I get an online second opinion from an NCI-designated cancer center oncologist without traveling?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. ByOnco provides comprehensive virtual second opinions and 1-on-1 video consultations with board-certified oncologists trained at leading US NCI-designated comprehensive cancer centers within 24 to 48 hours.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title="Find Cancer Doctors & Leading Oncologists | NPPES Registry Directory | ByOnco"
        description="Search board-certified medical oncologists, radiation oncologists, and surgical oncologists across all 50 US states. Verified CMS NPPES NPI Registry directory."
        path="/find-oncologists"
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">Find Oncologists</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/cancer-second-opinion" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Get Expert Second Opinion <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative pt-12 pb-14 bg-gradient-to-b from-emerald-50/60 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-5">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            CMS NPPES NPI Verified · 50 US States · All Oncology Specialties
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            Find Cancer Doctors & Leading Oncologists
          </h1>
          <p className="max-w-3xl mx-auto text-base sm:text-xl text-charcoal/85 leading-relaxed mb-8">
            Access verified practice addresses, direct office phone numbers, and taxonomy credentials for board-certified medical, surgical, and radiation oncologists across the United States.
          </p>

          {/* Search Toolbar */}
          <div className="max-w-4xl mx-auto bg-white p-4 sm:p-5 rounded-2xl border border-border-mist shadow-md flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-5 h-5 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by state name, city, or oncologist..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-forest-ink text-sm sm:text-base text-charcoal"
              />
            </div>

            <div className="w-full md:w-60">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-forest-ink text-sm sm:text-base bg-white text-charcoal"
              >
                <option value="all">All 50 US States</option>
                {US_STATES.map((s) => (
                  <option key={s.slug} value={s.slug}>
                    {s.name} ({s.abbr})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Section 1: Top US Oncology Metro Cities (10+ Providers) */}
        <section>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
                High-Density Metros
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mt-2 mb-1">
                Top US Cities with Verified Oncology Networks
              </h2>
              <p className="text-sm text-charcoal/80">
                Explore dedicated directories for metropolitan areas with 10+ active board-certified oncologists.
              </p>
            </div>
            <Link
              to="/cancer-centers-by-state"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-800 hover:text-forest-ink transition"
            >
              Browse Cancer Centers Directory <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {QUALIFIED_CITIES.map((city) => (
              <Link
                key={city.path}
                to={city.path}
                className="group p-4 rounded-xl bg-white border border-border-mist hover:border-emerald-600 hover:shadow-md transition flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">
                      {city.stateAbbr}
                    </span>
                    <span className="text-xs text-gray-500 font-medium">
                      {city.providerCount} Providers
                    </span>
                  </div>
                  <h3 className="font-bold text-forest-ink text-base group-hover:text-emerald-800 transition">
                    {city.cityName}
                  </h3>
                  <p className="text-xs text-charcoal/70 line-clamp-2 mt-1">
                    {city.stateName} verified oncology practice locations & contacts.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border-mist flex items-center justify-between text-xs font-semibold text-emerald-800">
                  <span>View Specialists</span>
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Section 2: Browse All 50 States Directory */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Browse Oncologists by US State
            </h2>
            <p className="text-sm sm:text-base text-charcoal/80">
              Select your state to explore licensed medical, surgical, and radiation oncologists with practice addresses and NPI registry verification.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {filteredStates.map((st) => {
              const providerCount = ONCOLOGISTS_BY_STATE[st.slug]?.length || 10;
              return (
                <Link
                  key={st.slug}
                  to={`/find-oncologists/${st.slug}`}
                  className="p-3.5 rounded-xl bg-white border border-border-mist hover:border-emerald-500 hover:bg-emerald-50/20 transition flex flex-col justify-between"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-emerald-900 bg-emerald-100 px-1.5 py-0.5 rounded">
                      {st.abbr}
                    </span>
                    <span className="text-[11px] text-gray-500">
                      {providerCount}+ Dr.
                    </span>
                  </div>
                  <p className="font-bold text-forest-ink text-sm truncate">
                    {st.name}
                  </p>
                  <div className="mt-2 flex items-center text-[11px] font-medium text-emerald-800">
                    <span>Directory</span>
                    <ChevronRight className="w-3 h-3 ml-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Section 3: Oncology Subspecialties & Taxonomy Codes */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="max-w-3xl mb-8">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Clinical Specialization
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mt-3 mb-2">
              Understanding Oncology Subspecialties & NPI Taxonomy Codes
            </h2>
            <p className="text-charcoal/80 text-sm sm:text-base">
              Cancer care is delivered by a multidisciplinary team of specialized physicians. Here is what each taxonomy code represents:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ONCOLOGY_TAXONOMIES.map((tax) => (
              <div key={tax.slug} className="p-5 rounded-xl bg-cream-paper/60 border border-border-mist">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    NPI: {tax.code}
                  </span>
                  <Stethoscope className="w-4 h-4 text-emerald-700" />
                </div>
                <h3 className="font-bold text-forest-ink text-base mb-1">
                  {tax.name}
                </h3>
                <p className="text-xs text-charcoal/80 leading-relaxed mb-3">
                  {tax.description}
                </p>
                <div className="pt-2 border-t border-border-mist text-xs text-emerald-800 font-semibold">
                  Provider Role: {tax.short}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4: Choosing the Right Oncologist Guide */}
        <section className="grid lg:grid-cols-2 gap-8 items-center bg-gradient-to-r from-emerald-900 to-forest-ink text-white rounded-3xl p-8 sm:p-12 shadow-xl">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-300 bg-emerald-800/80 px-3 py-1 rounded-full">
              Patient Empowerment
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold font-serif mt-4 mb-4 leading-tight">
              How to Choose the Right Oncologist for Your Care
            </h2>
            <p className="text-emerald-100/90 text-sm sm:text-base leading-relaxed mb-6">
              A cancer diagnosis demands an oncologist with deep expertise in your exact disease histology and molecular subtype. Consider these crucial criteria:
            </p>
            <ul className="space-y-3 text-xs sm:text-sm text-emerald-50 mb-8">
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong>Disease Volume:</strong> Oncologists treating 50+ cases of your specific cancer annually have significantly better outcome rates.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong>Multidisciplinary Tumor Board:</strong> Ensure your physician presents complex staging decisions to a joint panel of surgeons, pathologists, and radiologists.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span><strong>Clinical Trial Access:</strong> Academic centers and NCI-designated networks offer novel targeted agents before commercial availability.</span>
              </li>
            </ul>
            <Link
              to="/cancer-second-opinion"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-forest-ink font-bold text-sm hover:bg-emerald-50 transition shadow-md"
            >
              Get a Virtual Second Opinion ($199) <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="bg-white/10 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-white/20 text-white">
            <h3 className="text-lg font-bold font-serif mb-4 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-emerald-300" /> Top Questions to Ask at Your First Consult
            </h3>
            <ol className="space-y-3 text-xs sm:text-sm text-emerald-100">
              <li className="p-3 rounded-lg bg-white/5 border border-white/10">
                1. What is the exact stage, histological grade, and molecular biomarker profile of my cancer?
              </li>
              <li className="p-3 rounded-lg bg-white/5 border border-white/10">
                2. What is the primary goal of this therapy: curative intent, disease control, or symptom palliation?
              </li>
              <li className="p-3 rounded-lg bg-white/5 border border-white/10">
                3. Are there active clinical trials or targeted therapies that match my next-generation sequencing report?
              </li>
              <li className="p-3 rounded-lg bg-white/5 border border-white/10">
                4. What are the common acute toxicities, and who do I call if I develop a fever or severe side effects after hours?
              </li>
            </ol>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
